import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { PageHead, card, inputCls } from "@/components/admin/kit";
import { VoiceSettings } from "@/components/admin/VoiceLibrary";
import { Switch } from "@/components/ui/switch";

const blobErr = async (e) => {
  let m = errMsg(e);
  if (e?.response?.data instanceof Blob) { try { m = JSON.parse(await e.response.data.text()).detail; } catch {} }
  return m;
};

function DisplayControls({ clip, onSaved }) {
  const api = useApi();
  const [order, setOrder] = useState(clip.display_order);
  const put = async (show, display_order) => {
    try { await api.patch(`/website-clips/${clip.key}/display`, { show, display_order }); toast.success(show ? `Showing on website (#${display_order})` : "Hidden from website"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-[var(--lav)] px-4 py-3" data-testid="clip-display">
      <label className="flex items-center gap-2 text-sm font-semibold">
        <Switch checked={clip.show} onCheckedChange={(v) => put(v, order)} data-testid="clip-show-switch" /> Show on website
      </label>
      <label className="flex items-center gap-2 text-sm font-semibold">Display order
        <input type="number" min={1} max={99} value={order} onChange={(e) => setOrder(Number(e.target.value))}
          onBlur={() => order !== clip.display_order && order >= 1 && put(clip.show, order)} className={`${inputCls} w-20`} data-testid="clip-order-input" />
      </label>
    </div>
  );
}

const Field = ({ label, testid, ...p }) => (
  <label className="grid gap-1 text-sm font-semibold">{label}
    <input className={`${inputCls} font-normal`} data-testid={testid} {...p} />
  </label>
);

function ComparePlayer({ web, phone }) {
  const ref = useRef(null);
  const resume = useRef(null);
  const [mode, setMode] = useState("phone");
  const pick = (m) => {
    if (m === mode) return;
    const a = ref.current;
    resume.current = a ? { t: a.currentTime, play: !a.paused } : null;
    setMode(m);
  };
  const onLoaded = () => {
    const a = ref.current, r = resume.current;
    if (!a || !r) return;
    a.currentTime = Math.min(r.t, a.duration || r.t);
    if (r.play) a.play();
    resume.current = null;
  };
  const tab = (m, label) => (
    <button type="button" onClick={() => pick(m)} disabled={m === "web" && !web} data-testid={`clip-compare-${m}`}
      className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-white"}`}>{label}</button>
  );
  return (
    <div className="flex w-full flex-wrap items-center gap-2 rounded-2xl border p-2" data-testid="clip-compare">
      <div className="flex rounded-full bg-[var(--lav)] p-1">{tab("web", "Website")}{tab("phone", "Phone 8 kHz")}</div>
      <audio ref={ref} controls autoPlay={!resume.current && mode === "phone"} src={mode === "web" ? web : phone} onLoadedMetadata={onLoaded}
        className="h-10 max-w-full" data-testid="clip-phone-audio" />
      <span className="text-xs text-muted-foreground" data-testid="clip-compare-mode">{mode === "web" ? "Playing website version" : "Playing phone version"} · switch keeps your place</span>
    </div>
  );
}

function ClipEditor({ clip, favs, onSaved }) {
  const api = useApi();
  const [text, setText] = useState(clip.text);
  const [info, setInfo] = useState({ title: clip.title, meta: clip.meta, note: clip.note || "" });
  const setI = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const [v, setV] = useState({ code: "te-IN", name: "Telugu", voice_options: favs, voice: clip.voice, pace: clip.pace, loudness: clip.loudness, pitch: clip.pitch, sample_rate: clip.sample_rate });
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [heard, setHeard] = useState("");
  const body = { ...info, text, voice: v.voice, pace: v.pace, loudness: v.loudness, pitch: v.pitch, sample_rate: v.sample_rate };
  const dirty = ["title", "meta", "note", "text", "voice", "pace", "loudness", "pitch", "sample_rate"].some((k) => body[k] !== (clip[k] ?? ""));
  const fresh = !!preview && heard === JSON.stringify(body);

  const listen = async () => {
    setBusy("preview");
    try {
      const r = await api.post(`/website-clips/${clip.key}/preview`, body, { responseType: "blob" });
      setPreview(URL.createObjectURL(r.data));
      setHeard(JSON.stringify(body));
    } catch (e) { toast.error(await blobErr(e)); } finally { setBusy(""); }
  };
  const [phone, setPhone] = useState(null);
  const phoneListen = async () => {
    setBusy("phone");
    try {
      const r = await api.post(`/website-clips/${clip.key}/phone`, body, { responseType: "blob" });
      setPhone(URL.createObjectURL(r.data));
    } catch (e) { toast.error(await blobErr(e)); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { await api.put(`/website-clips/${clip.key}`, body); toast.success(heard === JSON.stringify(body) ? "Saved — the website now plays the exact take you heard" : "Saved — a new recording was made for the website"); setPreview(null); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className={`${card} grid gap-4`} data-testid={`clip-editor-${clip.key}`}>
      <div>
        <p className="text-lg font-bold">{clip.title}</p>
        <p className="text-xs text-muted-foreground">{clip.meta}</p>
      </div>
      <DisplayControls clip={clip} onSaved={onSaved} />
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Title" value={info.title} onChange={setI("title")} maxLength={120} testid="clip-title" />
        <Field label="Subtitle" value={info.meta} onChange={setI("meta")} maxLength={200} testid="clip-meta" />
      </div>
      <Field label="One-line website explanation (optional)" value={info.note} onChange={setI("note")} maxLength={200} testid="clip-note" />
      <label className="grid gap-1 text-sm font-semibold">Script
        <textarea className={`${inputCls} font-te min-h-[180px] font-normal`} maxLength={3500} value={text} onChange={(e) => setText(e.target.value)} data-testid="clip-text" />
        <span className="text-xs font-normal text-muted-foreground" data-testid="clip-char-count">{text.length}/3500 characters</span>
      </label>
      <VoiceSettings lang={v} setLang={setV} />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={listen} disabled={!text.trim() || !!busy} data-testid="clip-preview-btn">
          {busy === "preview" ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />} Listen before saving
        </Button>
        <Button variant="outline" onClick={phoneListen} disabled={!text.trim() || !!busy} data-testid="clip-phone-btn">
          {busy === "phone" ? <Loader2 size={16} className="animate-spin" /> : <Phone size={16} />} Phone quality
        </Button>
        <Button onClick={save} disabled={(!dirty && !fresh) || !text.trim() || !!busy} data-testid="clip-save-btn">
          {busy === "save" ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : fresh ? "Use this take on website" : "Save & update website"}
        </Button>
        {preview && <audio controls autoPlay src={preview} className="h-10" data-testid="clip-preview-audio" />}
        {phone && <ComparePlayer web={fresh ? preview : clip.audio_url ? `${process.env.REACT_APP_BACKEND_URL}${clip.audio_url}` : null} phone={phone} />}
        {preview && heard !== JSON.stringify(body) && <span className="text-xs font-semibold text-orange-700" data-testid="clip-preview-stale">Settings changed since you listened — Save will make a new take</span>}
      </div>
      <div className="rounded-2xl bg-[var(--lav)] p-3 text-sm" data-testid="clip-live">
        <p className="font-semibold">On the website now · Voice: <span data-testid="clip-live-voice">{clip.voice_name}</span></p>
        {clip.audio_url
          ? <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${clip.audio_url}`} className="mt-2 h-10 w-full max-w-md" data-testid="clip-live-audio" />
          : <p className="text-xs text-muted-foreground">No recording yet — press Save to make one.</p>}
        <p className="mt-1 text-xs text-muted-foreground">pace {clip.pace} · loudness {clip.loudness} · pitch {clip.pitch} · {clip.sample_rate / 1000}k{clip.duration ? ` · ${clip.duration}s` : ""}</p>
      </div>
    </div>
  );
}

export default function WebsiteClips() {
  const [clips, reload] = useLoad("/website-clips");
  const [langs] = useLoad("/languages");
  const [sel, setSel] = useState(null);
  useEffect(() => { if (clips?.length && !sel) setSel(clips[0].key); }, [clips, sel]);
  const favs = (langs || []).find((l) => l.code === "te-IN")?.voice_options || [];
  const clip = (clips || []).find((c) => c.key === sel);
  return (
    <div data-testid="website-clips-page">
      <PageHead title="Website voice clips" sub="Only for the public website's sample calls. Not used for hospitals, pharmacies or patients." />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="clip-list">
        {(clips || []).map((c, i) => (
          <button key={c.key} onClick={() => setSel(c.key)} data-testid={`clip-tab-${c.key}`}
            className={`rounded-2xl border px-4 py-2 text-left text-sm transition-colors ${sel === c.key ? "border-[var(--violet)] bg-[var(--violet)] text-white" : "border-[var(--lav-3)] bg-white hover:bg-[var(--lav)]"}`}>
            <span className="block text-[10px] font-bold uppercase tracking-wider opacity-70">Sample {String(i + 1).padStart(2, "0")}{c.show ? ` · on website #${c.display_order}` : ""}</span>
            <span className="font-semibold">{c.voice_name}</span> <span className="opacity-70">· {c.ready ? `${c.duration}s` : "no recording"}</span>
            <span className="block truncate text-xs opacity-70">{c.title}</span>
          </button>
        ))}
      </div>
      {clip && <ClipEditor key={`${clip.key}-${clip.generated_at}`} clip={clip} favs={favs} onSaved={reload} />}
    </div>
  );
}
